import type { AxiosInstance } from 'axios';

import type { productImport as pi } from '@terra/contracts';

export interface Paged<T> {
  data: T[];
  page: { page: number; pageSize: number; total: number; pageCount: number };
}

/** Bulk product import — job lifecycle, row-level error log and history. */
export function importEndpoints(http: AxiosInstance) {
  return {
    /** Shipped vendor mapping + option defaults, for pre-filling the run form. */
    getDefaults: () =>
      http.get<pi.ImportDefaults>('/admin/catalog/import/defaults').then((r) => r.data),

    createJob: (body: {
      sourceName: string;
      sourceUrl: string;
      mode: pi.ImportJobMode;
      options?: Partial<pi.ImportOptions>;
    }) => http.post<pi.ImportJob>('/admin/catalog/import/jobs', body).then((r) => r.data),

    listJobs: (params?: { page?: number; pageSize?: number; status?: pi.ImportJobStatus }) =>
      http.get<Paged<pi.ImportJob>>('/admin/catalog/import/jobs', { params }).then((r) => r.data),

    getJob: (jobId: string) =>
      http.get<pi.ImportJob>(`/admin/catalog/import/jobs/${jobId}`).then((r) => r.data),

    listRows: (
      jobId: string,
      params?: { page?: number; pageSize?: number; status?: pi.ImportRowStatus },
    ) =>
      http
        .get<Paged<pi.ImportRow>>(`/admin/catalog/import/jobs/${jobId}/rows`, { params })
        .then((r) => r.data),

    pause: (jobId: string) =>
      http.post<pi.ImportJob>(`/admin/catalog/import/jobs/${jobId}/pause`).then((r) => r.data),

    resume: (jobId: string) =>
      http.post<pi.ImportJob>(`/admin/catalog/import/jobs/${jobId}/resume`).then((r) => r.data),

    cancel: (jobId: string) =>
      http.post<pi.ImportJob>(`/admin/catalog/import/jobs/${jobId}/cancel`).then((r) => r.data),

    retryFailed: (jobId: string) =>
      http
        .post<{
          job: pi.ImportJob;
          requeued: number;
        }>(`/admin/catalog/import/jobs/${jobId}/retry-failed`)
        .then((r) => r.data),

    /** Roll a completed run back to the state the catalog was in before it. */
    undo: (jobId: string) =>
      http.post<pi.ImportJob>(`/admin/catalog/import/jobs/${jobId}/undo`).then((r) => r.data),

    deleteJob: (jobId: string) =>
      http.delete(`/admin/catalog/import/jobs/${jobId}`).then((r) => r.data),
  };
}

export type ImportEndpoints = ReturnType<typeof importEndpoints>;
